import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services';
import { useAuthStore } from '../../stores/authStore';
import BackButton from './components/BackButton';
import OtpHeader from './components/OtpHeader';
import ErrorAlert from './components/ErrorAlert';
import SubmitButton from './components/SubmitButton';
import AuthBackground from './components/AuthBackground';
import { normalizeOtp, receiveSmsOtp, startSmsOtp, cancelSmsOtp } from './webOtp';

const OTPPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOTP, isAuthenticated } = useAuthStore();
  const phoneNumber = location.state?.phoneNumber;

  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(120);
  const inputRef = useRef<HTMLInputElement>(null);
  const verifying = useRef(false);
  const mounted = useRef(true);
  useEffect(()=>{mounted.current=true;return ()=>{mounted.current=false;};},[]);

  useEffect(() => {
    if (!phoneNumber) {
      navigate('/', { replace: true });
    }
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [phoneNumber, isAuthenticated, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const verifyCode = useCallback(async (code: string) => {
    if (!phoneNumber || code.length !== 6) {
      setError('لطفا کد ۶ رقمی را به طور کامل وارد کنید');
      return;
    }

    if(verifying.current)return;
    verifying.current=true;
    cancelSmsOtp();
    setLoading(true);
    setError('');

    try {
      await verifyOTP(phoneNumber, code);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const errorData = err.response?.data?.error;
      const msg = errorData?.message_fa || errorData?.message || 'کد وارد شده صحیح نیست';
      setError(msg);
      setOtp(['', '', '', '', '', '']);
      inputRef.current?.focus();
    } finally {
      verifying.current=false;
      setLoading(false);
    }
  }, [navigate, phoneNumber, verifyOTP]);

  useEffect(() => {
    if(!phoneNumber)return;
    let active=true;
    void receiveSmsOtp(phoneNumber).then(code=>{
      if(active&&code){setOtp(code.split(''));void verifyCode(code);}
    });
    return ()=>{active=false;cancelSmsOtp();};
  }, [phoneNumber, verifyCode]);

  const handleCodeChange=(value:string)=>{
    const code=normalizeOtp(value);
    setOtp(Array.from({length:6},(_,i)=>code[i]||''));
    setError('');
    if(code.length===6)void verifyCode(code);
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    setError('');

    try {
      const sms=startSmsOtp(phoneNumber);
      await authService.sendOTP(phoneNumber);
      void sms.then(code=>{if(mounted.current&&code&&!verifying.current){setOtp(code.split(''));void verifyCode(code);}});
      setCountdown(120);
      setOtp(['', '', '', '', '', '']);
      inputRef.current?.focus();
    } catch (err: any) {
      const errorData = err.response?.data?.error;
      cancelSmsOtp();
      setError(errorData?.message_fa || errorData?.message || 'خطا در ارسال مجدد کد');
    } finally {
      setResendLoading(false);
    }
  };

  if (!phoneNumber) {
    return null;
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <main
      role="main"
      aria-labelledby="otp-title"
      className="min-h-screen w-full flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-x-hidden overflow-y-auto"
    >
      <AuthBackground />
      
      <div className="w-full max-w-[440px] relative z-10">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl sm:rounded-[32px] shadow-2xl p-5 min-[360px]:p-6 sm:p-10 border border-white/20">

          {/* Back Button */}
          <BackButton onClick={() => navigate('/', { replace: true })} />

          <a
            href="/"
            className="mb-5 inline-flex min-h-11 items-center gap-2 rounded-xl px-3 text-sm font-bold text-[#0e444e] transition-colors hover:bg-[#0e444e]/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#b58a49]"
            aria-label="بازگشت به وب‌سایت راز"
          >
            <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m6-6-6 6 6 6" />
            </svg>
            بازگشت به وب‌سایت
          </a>

          {/* Logo Section */}
          <OtpHeader phoneNumber={phoneNumber} />

          {/* OTP Inputs */}
          <form autoComplete="on" onSubmit={e=>{e.preventDefault();void verifyCode(otp.join(''));}}>
          <div className="mb-10">
            <div
              role="group"
              aria-label="کد تایید ۶ رقمی"
              aria-describedby="otp-page-instructions"
              className="flex justify-center gap-1.5 min-[360px]:gap-2 sm:gap-4"
              dir="ltr"
            >
              <p id="otp-page-instructions" className="sr-only">
                کد تأیید ۶ رقمی را وارد کنید یا پیشنهاد کد پیامک را از بالای کیبورد انتخاب کنید. چسباندن کد نیز امکان‌پذیر است.
              </p>
              <input
                ref={inputRef}
                type="text"
                name="one-time-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="done"
                value={otp.join('')}
                onChange={e=>handleCodeChange(e.target.value)}
                onPaste={e=>{e.preventDefault();handleCodeChange(e.clipboardData.getData('text'));}}
                disabled={loading}
                aria-label="کد تأیید ۶ رقمی"
                aria-describedby="otp-page-instructions"
                aria-invalid={!!error}
                className="w-full h-16 text-center text-3xl tracking-[0.35em] font-bold text-[#0e444e] bg-gray-50 border-2 border-gray-300 rounded-xl focus:border-[#b58a49] focus:ring-4 focus:ring-[#d5b481]/25 outline-none disabled:opacity-50"
              />
            </div>
          </div>

          {/* Error */}
          <ErrorAlert text={error} />

          {/* Verify Button */}
          <SubmitButton
            type="submit"
            disabled={loading || otp.some(d => !d)}
            loading={loading}
          >
            تایید و ورود
          </SubmitButton>
          <p className="mt-4 text-center text-xs leading-6 text-gray-500">اگر کد بالای کیبورد پیشنهاد شد، آن را لمس کنید؛ وارد کردن دستی کد هم امکان‌پذیر است.</p>
          </form>

          {/* Resend */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm font-medium mb-3">
              کد را دریافت نکردید؟
            </p>
            {countdown > 0 ? (
              <div role="status" aria-live="polite" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0e444e]/5 rounded-xl text-[#0e444e] font-bold text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span dir="ltr">{formatTime(countdown)}</span>
                <span>تا ارسال مجدد</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resendLoading}
                className="text-[#d5b481] hover:text-[#b8944d] font-bold text-sm underline decoration-2 underline-offset-4 transition-colors disabled:opacity-50"
              >
                {resendLoading ? 'در حال ارسال...' : 'ارسال مجدد کد'}
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default OTPPage;
